import { Link } from 'react-router-dom';
import { Car, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, Zap, ChevronRight } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="container py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">AutoSearch</span>
                <span className="text-blue-400">SA</span>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-4 italic">
              "Need a new car? No need to look far."
            </p>
            <p className="text-sm text-gray-400 mb-4">
              South Africa's premier car search platform. Find, compare, and buy your perfect vehicle from 173+ trusted dealers.
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-9 h-9 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 bg-gray-800 hover:bg-blue-400 rounded-lg flex items-center justify-center transition">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 bg-gray-800 hover:bg-pink-600 rounded-lg flex items-center justify-center transition">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 bg-gray-800 hover:bg-blue-700 rounded-lg flex items-center justify-center transition">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Buy a Car */}
          <div>
            <h4 className="text-white font-semibold mb-4">Buy a Car</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/results" className="text-gray-400 hover:text-white transition">Search Cars</Link></li>
              <li><Link to="/help-me-find" className="text-gray-400 hover:text-white transition">Help Me Find</Link></li>
              <li><Link to="/dealers" className="text-gray-400 hover:text-white transition">Find Dealers</Link></li>
              <li><Link to="/ev-charging" className="text-gray-400 hover:text-yellow-400 transition flex items-center gap-1">
                <Zap className="w-3 h-3" />EV Charging
              </Link></li>
              <li><Link to="/news" className="text-gray-400 hover:text-white transition">News & Reviews</Link></li>
            </ul>
          </div>

          {/* Sell a Car */}
          <div>
            <h4 className="text-white font-semibold mb-4">Sell a Car</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/sell-your-car" className="text-gray-400 hover:text-white transition">Sell Your Car</Link></li>
              <li><Link to="/dealer-portal" className="text-gray-400 hover:text-white transition">Dealer Portal</Link></li>
            </ul>
          </div>

          {/* Finance & Insurance */}
          <div>
            <h4 className="text-white font-semibold mb-4">Finance</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/affordability" className="text-gray-400 hover:text-white transition">What Can I Afford?</Link></li>
              <li><Link to="/finance" className="text-gray-400 hover:text-white transition">Finance Calculator</Link></li>
              <li><Link to="/insurance" className="text-gray-400 hover:text-white transition">Insurance Quotes</Link></li>
              <li><Link to="/life-cover" className="text-gray-400 hover:text-white transition">Life Cover</Link></li>
              <li><Link to="/inspector" className="text-gray-400 hover:text-white transition">Car Inspector</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-gray-400 hover:text-white transition">About Us</Link></li>
              <li><Link to="/faq" className="text-gray-400 hover:text-white transition">FAQ</Link></li>
              <li><Link to="/advice" className="text-gray-400 hover:text-white transition">Buying Advice</Link></li>
              <li><Link to="/terms" className="text-gray-400 hover:text-white transition">Terms of Service</Link></li>
              <li><Link to="/privacy" className="text-gray-400 hover:text-white transition">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>

        {/* Popular Searches */}
        <div className="mt-10 pt-8 border-t border-gray-800">
          <h4 className="text-white font-semibold mb-4">Popular Searches</h4>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Toyota Hilux', link: '/results?make=Toyota&model=Hilux' },
              { label: 'VW Polo', link: '/results?make=Volkswagen&model=Polo' },
              { label: 'Ford Ranger', link: '/results?make=Ford&model=Ranger' },
              { label: 'BMW 3 Series', link: '/results?make=BMW&model=3%20Series' },
              { label: 'Mercedes C-Class', link: '/results?make=Mercedes-Benz&model=C-Class' },
              { label: 'Cars Under R150k', link: '/results?max_price=150000' },
              { label: 'SUVs Under R500k', link: '/results?body_type=SUV&max_price=500000' },
              { label: 'Electric Vehicles', link: '/results?fuel=Electric' },
              { label: 'Automatic Cars', link: '/results?transmission=Automatic' },
              { label: 'Cars in Gauteng', link: '/results?province=Gauteng' },
              { label: 'Cars in Cape Town', link: '/results?province=Western%20Cape' }
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.link}
                className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-full text-xs transition"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Contact Bar */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6 text-sm">
              <a href="mailto:autosearchsa@gmail.com" className="flex items-center gap-2 text-gray-400 hover:text-white transition">
                <Mail className="w-4 h-4 text-blue-400" />
                autosearchsa@gmail.com
              </a>
              <div className="flex items-center gap-2 text-gray-400">
                <MapPin className="w-4 h-4 text-red-400" />
                Johannesburg, South Africa
              </div>
            </div>
            <Link
              to="/dealer-portal"
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition"
            >
              List Your Dealership
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-gray-950">
        <div className="container py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <p className="text-gray-500">
              © {new Date().getFullYear()} AutoSearchSA. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-gray-500">
              <Link to="/terms" className="hover:text-white transition">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
              <Link to="/about" className="hover:text-white transition">About</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
